import { Link } from "react-router-dom";
import {
  useAlerts,
  useDevices,
  useFleetTrend,
  usePatchSummary,
} from "../hooks/queries";
import {
  formatMetricValue,
  metricLabel,
  timeFormatterForRange,
} from "../hooks/metrics";
import { CHART_COLORS, TimeSeriesChart } from "../components/charts/TimeSeriesChart";
import {
  AlertStateBadge,
  DeviceStatusBadge,
  SeverityBadge,
} from "../components/domain/Badges";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/Card";
import { Skeleton } from "../components/ui/Skeleton";
import { ErrorState, EmptyState } from "../components/ui/States";
import { Table, TBody, TD, TH, THead, TR } from "../components/ui/Table";
import { formatInteger, formatPercent, formatRelative } from "../lib/format";

export function DashboardPage() {
  const devicesQuery = useDevices({ perPage: 100 });
  const alertsQuery = useAlerts({ perPage: 6 });
  const patchQuery = usePatchSummary(1, 100);

  const devices = devicesQuery.data?.items ?? [];
  const sampleIds = devices
    .filter((device) => device.status !== "offline")
    .slice(0, 6)
    .map((device) => device.id);

  const cpuTrend = useFleetTrend(sampleIds, "cpu.usage_percent", "24h");
  const memoryTrend = useFleetTrend(sampleIds, "memory.usage_percent", "24h");

  const patchItems = patchQuery.data?.items ?? [];
  const compliantDevices = patchItems.filter((item) => item.pendingCount === 0).length;
  const complianceRate =
    patchItems.length > 0 ? (compliantDevices / patchItems.length) * 100 : 0;
  const criticalAlerts = (alertsQuery.data?.items ?? []).filter(
    (alert) => alert.state === "firing" && alert.severity === "critical",
  ).length;

  const online = devices.filter((device) => device.status === "online").length;
  const offline = devices.filter((device) => device.status === "offline").length;

  const summaryCards = [
    { label: "Toplam cihaz", value: formatInteger(devices.length), hint: "Yönetilen uç noktalar" },
    { label: "Çevrimiçi", value: formatInteger(online), hint: "Heartbeat alan cihazlar" },
    { label: "Çevrimdışı", value: formatInteger(offline), hint: "Yanıt vermeyen cihazlar" },
    { label: "Kritik uyarı", value: formatInteger(criticalAlerts), hint: "Tetiklenmiş kritik uyarılar" },
    {
      label: "Patch uyumluluğu",
      value: formatPercent(complianceRate),
      hint: `${compliantDevices}/${patchItems.length} cihaz güncel`,
    },
    {
      label: "Bekleyen yama",
      value: formatInteger(
        patchItems.reduce((total, item) => total + item.pendingCount, 0),
      ),
      hint: "Filo genelinde",
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-xl font-semibold tracking-tight">Gösterge Paneli</h1>
        <p className="text-muted-foreground mt-1 text-sm">
          Filo özeti, eğilimler ve son uyarılar.
        </p>
      </div>

      {/* Özet kartları */}
      {devicesQuery.isPending ? (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-3 xl:grid-cols-6">
          {Array.from({ length: 6 }).map((_, index) => (
            <Skeleton key={index} className="h-24" />
          ))}
        </div>
      ) : devicesQuery.isError ? (
        <ErrorState
          message="Filo özeti yüklenemedi."
          onRetry={() => void devicesQuery.refetch()}
        />
      ) : (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-3 xl:grid-cols-6">
          {summaryCards.map((card) => (
            <Card key={card.label}>
              <CardContent className="pt-5">
                <p className="text-muted-foreground text-xs font-medium">{card.label}</p>
                <p className="mt-1 text-2xl font-semibold tracking-tight">{card.value}</p>
                <p className="text-muted-foreground mt-1 text-xs">{card.hint}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Trend grafikleri */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>CPU kullanım trendi (24 saat)</CardTitle>
            <CardDescription>
              Seçili {sampleIds.length} cihaz ortalaması
            </CardDescription>
          </CardHeader>
          <CardContent>
            {cpuTrend.isPending ? (
              <Skeleton className="h-56" />
            ) : cpuTrend.isError ? (
              <ErrorState message="CPU trendi yüklenemedi." onRetry={() => void cpuTrend.refetch()} />
            ) : cpuTrend.data && cpuTrend.data.length > 0 ? (
              <TimeSeriesChart
                points={cpuTrend.data}
                color={CHART_COLORS.cpu}
                ariaLabel={`${metricLabel("cpu.usage_percent")} trend grafiği`}
                formatValue={(value) => formatMetricValue("cpu.usage_percent", value)}
                formatTime={timeFormatterForRange("24h")}
              />
            ) : (
              <EmptyState title="Veri yok" description="Seçili cihazlar için metrik bulunamadı." />
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Bellek kullanım trendi (24 saat)</CardTitle>
            <CardDescription>
              Seçili {sampleIds.length} cihaz ortalaması
            </CardDescription>
          </CardHeader>
          <CardContent>
            {memoryTrend.isPending ? (
              <Skeleton className="h-56" />
            ) : memoryTrend.isError ? (
              <ErrorState message="Bellek trendi yüklenemedi." onRetry={() => void memoryTrend.refetch()} />
            ) : memoryTrend.data && memoryTrend.data.length > 0 ? (
              <TimeSeriesChart
                points={memoryTrend.data}
                color={CHART_COLORS.memory}
                ariaLabel={`${metricLabel("memory.usage_percent")} trend grafiği`}
                formatValue={(value) => formatMetricValue("memory.usage_percent", value)}
                formatTime={timeFormatterForRange("24h")}
              />
            ) : (
              <EmptyState title="Veri yok" description="Seçili cihazlar için metrik bulunamadı." />
            )}
          </CardContent>
        </Card>
      </div>

      {/* Son uyarılar */}
      <Card>
        <CardHeader className="flex-row items-center justify-between">
          <div>
            <CardTitle>Son uyarılar</CardTitle>
            <CardDescription>En güncel 6 uyarı kaydı</CardDescription>
          </div>
          <Link
            to="/alerts"
            className="text-primary text-sm font-medium hover:underline focus-visible:ring-ring rounded focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-background focus-visible:outline-none"
          >
            Tümünü gör →
          </Link>
        </CardHeader>
        <CardContent>
          {alertsQuery.isPending ? (
            <Skeleton className="h-48" />
          ) : alertsQuery.isError ? (
            <ErrorState message="Uyarılar yüklenemedi." onRetry={() => void alertsQuery.refetch()} />
          ) : (alertsQuery.data?.items.length ?? 0) === 0 ? (
            <EmptyState title="Uyarı yok" description="Şu anda kayıtlı uyarı bulunmuyor." />
          ) : (
            <Table>
              <THead>
                <TR>
                  <TH>Cihaz</TH>
                  <TH>Mesaj</TH>
                  <TH>Önem</TH>
                  <TH>Durum</TH>
                  <TH>Başlangıç</TH>
                </TR>
              </THead>
              <TBody>
                {alertsQuery.data?.items.map((alert) => (
                  <TR key={alert.id}>
                    <TD>
                      <Link
                        to={`/devices/${alert.deviceId}`}
                        className="text-primary font-medium hover:underline"
                      >
                        {alert.hostname}
                      </Link>
                    </TD>
                    <TD className="max-w-md truncate" title={alert.message}>
                      {alert.message}
                    </TD>
                    <TD>
                      <SeverityBadge severity={alert.severity} />
                    </TD>
                    <TD>
                      <AlertStateBadge state={alert.state} />
                    </TD>
                    <TD className="text-muted-foreground whitespace-nowrap">
                      {formatRelative(alert.startedAt)}
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Durum dağılımı */}
      <Card>
        <CardHeader>
          <CardTitle>Cihaz sağlık dağılımı</CardTitle>
          <CardDescription>Duruma göre cihaz sayısı</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            {(["online", "degraded", "offline", "unknown"] as const).map((status) => (
              <div
                key={status}
                className="border-border flex items-center gap-2 rounded-lg border px-4 py-2"
              >
                <DeviceStatusBadge status={status} />
                <span className="text-sm font-semibold">
                  {devices.filter((device) => device.status === status).length}
                </span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}